import { MyBetLiveStreaming as MyBetLiveStreamingIcon } from '@ps/isp-web-component-library/icons/MyBetLiveStreaming';
import type { MyBetPart } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { BvpPlayer } from '@swf/sports/components/BvpPlayer/BvpPlayer';
import LiveOptions from '@swf/sports/components/LiveOptions/LiveOptions';
import { LiveOption } from '@swf/sports/types/liveOption';

import { useSportsMyBetsWidget } from '../../../../../../model';

import styles from './LiveOptionsPanel.scss';

interface Props {
  part: MyBetPart;
  betId?: string;
  accordionIds?: string[];
}

const LiveOptionsPanel = ({ part, betId, accordionIds }: Props) => {
  const isMyBetLiveStreamingEnabled = useFeature(FeatureFlag.ISPEnableMyBetsLiveStreaming);
  const isMyBetDataVisualisationEnabled = useFeature(FeatureFlag.ISPEnableMyBetsDataVisualisation);
  const { widgetSelect, widgetDispatch } = useSportsMyBetsWidget();
  const selectedLiveOption = useSelector(widgetSelect.selectedLiveOption);

  if (!isMyBetDataVisualisationEnabled && !isMyBetLiveStreamingEnabled) return null;
  if (!('hasLiveDataAvailable' in part)) return null;

  const {
    eventDescription,
    eventTypeId,
    inPlay,
    eventId,
    marketId,
    selectionId,
    hasLiveDataAvailable,
    hasLiveStreamAvailable
  } = part;

  const uniquePartId = `${betId}-${eventId}-${marketId}-${selectionId}`;
  const isSelectedLiveOption = selectedLiveOption.id === uniquePartId;
  const isLiveDataAvailable = isMyBetDataVisualisationEnabled && hasLiveDataAvailable;
  const isLiveStreamAvailable = isMyBetLiveStreamingEnabled && hasLiveStreamAvailable;

  const handleToggleGroupChange = (liveOption?: LiveOption) => {
    if (liveOption) {
      widgetDispatch.setSelectedLiveOption({
        id: uniquePartId,
        accordionIds,
        liveOption
      });
    } else {
      widgetDispatch.setSelectedLiveOption({});
    }
  };

  return 'hasLiveStreamAvailable' in part &&
    inPlay &&
    (isLiveDataAvailable || isLiveStreamAvailable) ? (
    <div className={styles.liveOptionsPanel}>
      <LiveOptions
        handleToggleGroupChange={handleToggleGroupChange}
        hasLiveDataAvailable={isLiveDataAvailable}
        hasLiveStreamAvailable={isLiveStreamAvailable}
        variant="plain"
        liveStreamIcon={MyBetLiveStreamingIcon}
      />

      {isSelectedLiveOption ? (
        <BvpPlayer
          eventId={Number(eventId)}
          name={eventDescription ?? ''}
          eventTypeId={eventTypeId}
          data-testid="bvp-iframe"
          streamType={selectedLiveOption.liveOption === LiveOption.LIVE_DATA ? 'viz' : 'vid'}
        />
      ) : null}
    </div>
  ) : null;
};

export default LiveOptionsPanel;
